import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { CalendarIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Separator } from "@/components/ui/separator/separator";
import { Calendar } from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea/textarea";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { createLeaveApplicationForEmployee } from "@/config/mutation/employee";

import { useAuth } from "@/lib/use-auth";
import { UploadAttachment } from "./upload-attachement";

const formSchema = z.object({
  leaveType: z.string(),
  startDate: z.date(),
  endDate: z.date(),
  reason: z.string(),
  isPaid: z.boolean().default(false),
  isHalfDay: z.boolean().default(false),
  attachment: z.string().optional(),
});

function differenceInDays(date1: Date, date2: Date): number {
  // Convert both dates to milliseconds
  const date1_ms: number = date1.getTime();
  const date2_ms: number = date2.getTime();

  // Calculate the difference in milliseconds
  const difference_ms: number = Math.abs(date2_ms - date1_ms);

  // Convert milliseconds to days
  const difference_days: number = Math.ceil(
    difference_ms / (1000 * 60 * 60 * 24)
  );

  return difference_days;
}

export type FormValues = z.infer<typeof formSchema>;

export const NewLeaveApplicationForm = () => {
  const empKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useAuth();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      leaveType: "",
      startDate: undefined,
      endDate: undefined,
      reason: "",
      attachment: "",
      isHalfDay: false,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createLeaveApplicationForEmployee(empKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    console.log(values);

    const totalDays = differenceInDays(values.startDate, values.endDate) + 1;

    console.log("total days", totalDays);

    const postData = {
      ...values,
      totalDays,
    };

    console.log("postData", postData);

    mutate(
      {
        ...values,
        totalDays,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leave-applications"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["employee", "leave-applications"],
          });

          toast.success("New Leave Application Created!");

          navigate("/employee/leave-applications", {
            replace: true,
          });
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING LEAVE APPLICATION", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  return (
    <div className="h-full rounded-md relative">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between sticky top-0 px-4 z-30 rounded-md">
            <h3 className="text-2xl font-medium">New Leave Application</h3>

            <Button type="submit" size="sm" disabled={isPending}>
              Save
            </Button>
          </div>

          <div className="py-5 px-5 mt-5 rounded-b-md">
            <div className="grid grid-cols-1 gap-4">
              <FormField
                control={form.control}
                name="leaveType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Leave Type</FormLabel>
                    <FormControl>
                      <Input type="text" disabled={isPending} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isPaid"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="leading-none flex flex-row gap-4">
                      <div className="space-y-1">
                        <FormLabel>Is Paid</FormLabel>
                        <FormDescription>
                          You can choose paid or unpaid leave
                        </FormDescription>
                      </div>

                      <Separator
                        orientation="vertical"
                        className="h-8 border"
                      />

                      <div className="bg-destructive flex items-center justify-center px-6 rounded text-xs">
                        You only have {data?.user?.paidLeavesRemaining} out of{" "}
                        {data?.user?.paidLeavesTotal} Paid leaves remaning.
                        {data?.user?.paidLeavesRemaining <= 0 &&
                          " You can apply for unpaid leave only."}
                      </div>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isHalfDay"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="leading-none flex flex-row gap-4">
                      <div className="space-y-1">
                        <FormLabel>Is Half Day</FormLabel>
                        <FormDescription>
                          Choose this if you plan to work atleast 5 hours
                        </FormDescription>
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <Separator className="my-4" />

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-[0.7] flex flex-col gap-3">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                      <FormLabel>From Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              type="button"
                              disabled={isPending}
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(day) => {
                              field.onChange(day);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col space-y-0 gap-3 mt-1">
                      <FormLabel>To Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              disabled={isPending}
                              type="button"
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            disabled={isPending}
                            mode="single"
                            selected={field.value}
                            onSelect={(day) => {
                              field.onChange(day);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex-1">
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reason</FormLabel>
                      <FormControl>
                        <Textarea
                          disabled={isPending}
                          className="h-full"
                          rows={5}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator className="my-4" />

            <div>
              <FormField
                control={form.control}
                name="attachment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Upload Attachment</FormLabel>
                    <FormControl>
                      <UploadAttachment
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};
